import { clsx } from 'clsx';
import type { CSSProperties, TransitionEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { spacingCSSVars } from '../../utils/spacing';
import type { AccordionContentProps } from './accordion.types';
import { useAutoScroll } from './accordionContext';
import { useItemExpanded, useItemId } from './accordionItemContext';

import styles from './accordion.module.css';

export const AccordionContent = ({
  allowOverflow = false,
  children,
  className,
  'data-testid': testId,
  destroyOnClose = false,
  duration = 250,
  id,
  style,
  ...spacingProps
}: AccordionContentProps) => {
  const { autoScrollMargin } = useAutoScroll();
  const isExpanded = useItemExpanded();
  const itemId = useItemId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [heading, setHeading] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);
  const [isFullyOpen, setIsFullyOpen] = useState(isExpanded);

  // Get DOM elements safely in useEffect for SSR compatibility
  useEffect(() => {
    setHeading(document.getElementById(`accordion-heading-${itemId}`));
    setContent(document.getElementById(`accordion-content-${itemId}`));
  }, [itemId]);

  // A11y fix: make collapsed content non-focusable
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const focusableElements = contentElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"]'
    );

    if (isExpanded) {
      focusableElements.forEach((element) => {
        element.setAttribute('tabindex', '0');
      });
    } else {
      focusableElements.forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    }
  }, [isExpanded]);

  // Sets overflow to visible once animation transition ends so we can use popovers/dropdowns/etc
  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (
      !allowOverflow ||
      event.target !== event.currentTarget ||
      event.propertyName !== 'grid-template-rows'
    ) {
      return;
    }
    setIsFullyOpen(isExpanded);
  };

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (!!autoScrollMargin && isExpanded) {
      timeout = setTimeout(() => {
        // Don't readjust the scroll if we're already pretty close
        if (heading && Math.abs(heading.offsetTop - window.scrollY) > 125) {
          heading.scrollIntoView({ behavior: 'smooth' });
        }
      }, duration + 10);
    }

    return () => {
      // Don't "follow" the closing accordion if we're already near the top (or above) it
      if (
        content &&
        content.offsetTop + content.getBoundingClientRect().height / 2 <
          window.scrollY
      ) {
        content.scrollIntoView({ behavior: 'smooth' });
      }

      timeout && clearTimeout(timeout);
    };
  }, [isExpanded, autoScrollMargin, heading, content, duration]);

  const animationVars = {
    '--transition-duration': `${duration}ms`,
  } as CSSProperties;

  const spacingVars = {
    ...spacingCSSVars(spacingProps),
  };

  return (
    <div
      className={clsx(styles['accordionContentWrapper'], {
        [styles['accordionContentWrapper__expanded']]: isExpanded,
      })}
      style={animationVars}
      onTransitionEnd={handleTransitionEnd}
    >
      {(isExpanded || !destroyOnClose) && (
        <div
          ref={contentRef}
          className={clsx(styles['accordionContent'], {
            [styles['accordionContent__animated']]: isExpanded,
            [styles['accordionContent__overflowVisible']]:
              allowOverflow && isExpanded && isFullyOpen,
          })}
          id={id ?? `accordion-content-${itemId}`}
          data-testid={testId ?? `accordion-content-${itemId}`}
          role="region"
          aria-labelledby={`accordion-heading-${itemId}`}
        >
          <div
            className={clsx(className, styles['accordionContent_inner'])}
            style={{ ...spacingVars, ...style }}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};
